/*
  Arctic Grey policy pages. Turns the plain policy body (Settings > Policies) into the editorial template.
  Markup contract for policy bodies: first <p> "Last updated: <date>", then a lead <p>, an optional <ul> of
  highlight cards (<li><strong>Title.</strong> text</li>), then <h2> sections (no numbers), each opening with
  <p><strong>In short:</strong> ...</p>. <h3>, lists and <table> are supported. No classes, ids or inline styles.
  Older bodies (June 2026 Terms, the 2023 Shopify privacy template, "Label: value" contact lines) are
  normalized first, see normalizeLegacy().
*/
(function () {
  'use strict';

  var POLICIES = {
    'terms-of-service': { label: 'Terms', title: 'Terms of service.', summary: 'The terms that govern every Arctic Grey engagement and your use of our website.' },
    'privacy-policy': { label: 'Privacy', title: 'Privacy policy.', summary: 'How Arctic Grey collects, uses and protects personal information.' },
    'refund-policy': { label: 'Refunds', title: 'Refund policy.', summary: 'How refunds work for Bulk Hours, audits, apps and products.' },
    'subscription-policy': { label: 'Subscriptions', title: 'Subscriptions and cancellations.', summary: 'How recurring plans are billed, changed, paused and cancelled.' },
    'shipping-policy': { label: 'Shipping', title: 'Shipping policy.', summary: 'How digital purchases and Arctic Grey merchandise are delivered.' },
    'contact-information': { label: 'Contact', title: 'Contact information.', summary: 'How to reach Arctic Grey.' },
    'ccpa-opt-out': { label: 'Your privacy choices', title: 'Your privacy choices.', summary: 'Opt out of advertising cookies and pixels sharing your visits.' },
    'legal-archive': { label: 'Legal archive', title: 'Legal archive.', summary: 'Earlier versions of our policies, kept for reference.' }
  };
  var ORDER = ['terms-of-service', 'privacy-policy', 'refund-policy', 'subscription-policy', 'shipping-policy', 'contact-information'];
  var WRAPPERS = { DIV: 1, SECTION: 1, ARTICLE: 1, MAIN: 1, HEADER: 1, FOOTER: 1 };
  var LEAD_MAX = 320;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        if (key === 'text') node.textContent = attrs[key];
        else if (key === 'html') node.innerHTML = attrs[key];
        else node.setAttribute(key, attrs[key]);
      });
    }
    (children || []).forEach(function (child) { if (child) node.appendChild(child); });
    return node;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function each(list, fn) { Array.prototype.slice.call(list).forEach(fn); }

  function rename(node, tag) {
    var next = document.createElement(tag);
    while (node.firstChild) next.appendChild(node.firstChild);
    node.parentNode.replaceChild(next, node);
    return next;
  }

  var chevron = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>';

  function linkify(node) {
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var hits = [];
    while (walker.nextNode()) { if (!walker.currentNode.parentNode.closest('a')) hits.push(walker.currentNode); }
    hits.forEach(function (text) {
      var parts = text.nodeValue.split(/([\w.+-]+@[\w-]+\.[\w.-]+|\+?\d[\d ()-]{8,}\d)/);
      if (parts.length < 2) return;
      var frag = document.createDocumentFragment();
      parts.forEach(function (part, i) {
        if (!part) return;
        if (i % 2 === 0) { frag.appendChild(document.createTextNode(part)); return; }
        var href = part.indexOf('@') > 0 ? 'mailto:' + part : 'tel:' + part.replace(/[^\d+]/g, '');
        frag.appendChild(el('a', { href: href, text: part }));
      });
      text.parentNode.replaceChild(frag, text);
    });
  }

  function detailsFrom(pairs) {
    var dl = el('dl', { class: 'agp-details' });
    pairs.forEach(function (pair) {
      var dd = el('dd', { html: pair.html });
      while (dd.firstChild && !dd.firstChild.textContent.replace(/ /g, ' ').trim()) dd.removeChild(dd.firstChild);
      linkify(dd);
      dl.appendChild(el('div', null, [el('dt', { text: pair.label }), dd]));
    });
    return dl;
  }

  /* Older policy bodies predate the markup contract. Map their structures onto the template's own. */
  function normalizeLegacy(root) {
    // Section numbers baked into the June 2026 Terms: the template numbers sections itself.
    each(root.querySelectorAll('.ag-sec__num'), function (n) { n.parentNode.removeChild(n); });
    each(root.querySelectorAll('h1, h2, h3'), function (h) { if (!h.textContent.trim()) h.parentNode.removeChild(h); });

    // The 2023 Shopify privacy template uses <h1> for sections and <h2> below them.
    if (root.querySelector('h1')) {
      each(root.querySelectorAll('h2'), function (h) { rename(h, 'h3'); });
      each(root.querySelectorAll('h1'), function (h) { rename(h, 'h2'); });
    }

    // Numbered clauses: <span class="cl"><span class="num">6.1</span>text</span>
    each(root.querySelectorAll('.ag-clauses .cl'), function (cl) {
      var num = cl.querySelector('.num');
      var p = el('p', { class: 'agp-clause' });
      if (num) {
        num.parentNode.removeChild(num);
        p.appendChild(el('span', { class: 'agp-clause__n', text: num.textContent.trim() }));
      }
      while (cl.firstChild) p.appendChild(cl.firstChild);
      cl.parentNode.replaceChild(p, cl);
    });

    // Definition rows: <div class="row">"Term"Definition</div>, grouped into one table like the new Terms.
    var lastTable = null;
    each(root.querySelectorAll('div.row'), function (row) {
      var m = row.innerHTML.match(/^\s*[“"]([^”"<]+)[”"]\s*([\s\S]*)$/);
      if (!m) return;
      var tr = el('tr', null, [el('td', { text: m[1].trim() }), el('td', { html: m[2].trim() })]);
      if (lastTable && row.previousElementSibling === lastTable) {
        lastTable.tBodies[0].appendChild(tr);
        row.parentNode.removeChild(row);
        return;
      }
      lastTable = el('table', null, [
        el('thead', null, [el('tr', null, [el('th', { text: 'Term' }), el('th', { text: 'Meaning' })])]),
        el('tbody', null, [tr])
      ]);
      row.parentNode.replaceChild(lastTable, row);
    });

    // Lettered lists: <li>(a) text</li> get a hanging marker instead of a dash.
    each(root.querySelectorAll('ul, ol'), function (list) {
      var items = Array.prototype.filter.call(list.children, function (li) { return li.tagName === 'LI'; });
      if (!items.length || !items.every(function (li) { return /^\s*\([a-z0-9]{1,4}\)\s/i.test(li.textContent); })) return;
      list.classList.add('agp-alpha');
      items.forEach(function (li) {
        var first = li.firstChild;
        if (!first || first.nodeType !== 3) return;
        var m = first.nodeValue.match(/^\s*(\([a-z0-9]{1,4}\))\s*/i);
        if (!m) return;
        first.nodeValue = first.nodeValue.slice(m[0].length);
        li.insertBefore(el('span', { class: 'agp-alpha__m', 'aria-hidden': 'true', text: m[1] }), first);
        li.insertBefore(el('span', { class: 'agp-sr', text: m[1] + ' ' }), first);
      });
    });

    each(root.querySelectorAll('.ag-caps'), function (p) { p.classList.add('agp-caps'); });
    each(root.querySelectorAll('.ag-contact .k'), function (k) { k.classList.add('agp-k'); });
    each(root.querySelectorAll('u'), function (u) { if (!u.closest('a')) u.classList.add('agp-u'); });

    // Runs of three or more short "Label: value" paragraphs (the 2023 contact page) become a details list.
    var run = [];
    function flush() {
      if (run.length >= 3) {
        var dl = detailsFrom(run.map(function (r) { return { label: r.label, html: r.html }; }));
        run[0].node.parentNode.insertBefore(dl, run[0].node);
        run.forEach(function (r) { r.node.parentNode.removeChild(r.node); });
      }
      run = [];
    }
    each(root.querySelectorAll('p'), function (p) {
      var text = p.textContent.replace(/\s+/g, ' ').trim();
      var m = text.match(/^([A-Z][A-Za-z ]{1,28}):\s*(\S.*)$/);
      var phone = !m && text.match(/^([A-Z][A-Za-z ]{1,28}?)\s+(\+?\d[\d ()-]{8,}\d)$/);
      var adjacent = run.length && run[run.length - 1].node.nextElementSibling === p;
      if ((!m && !phone) || text.length > 160 || /^(last updated|in short)/i.test(text)) { flush(); return; }
      if (run.length && !adjacent) flush();
      var html = m ? p.innerHTML.replace(/^[\s\S]*?:\s*(?:<\/[a-z]+>\s*)*/i, '') : phone[2];
      run.push({ node: p, label: (m || phone)[1].trim(), html: html });
    });
    flush();
  }

  function slugify(text, used) {
    var base = text.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
    var slug = base;
    var i = 2;
    while (used[slug] || document.getElementById(slug)) { slug = base + '-' + i++; }
    used[slug] = true;
    return slug;
  }

  function flatten(root) {
    var out = [];
    Array.prototype.forEach.call(root.childNodes, function (node) {
      if (node.nodeType === 3) {
        if (node.textContent.trim()) out.push(el('p', { text: node.textContent.trim() }));
      } else if (node.nodeType === 1) {
        if (WRAPPERS[node.tagName]) out = out.concat(flatten(node));
        else if (node.tagName !== 'META' && node.tagName !== 'STYLE' && node.tagName !== 'SCRIPT') out.push(node);
      }
    });
    return out;
  }

  function readData() {
    var tag = document.getElementById('agp-data');
    if (!tag) return {};
    try { return JSON.parse(tag.textContent) || {}; } catch (e) { return {}; }
  }

  function slugFromPath(path) {
    var parts = (path || '').split('?')[0].split('/').filter(Boolean);
    return (parts[parts.length - 1] || '').replace(/\.html$/, '');
  }

  function policyList(data) {
    var list = (data.policies || []).map(function (p) { return { slug: slugFromPath(p.url), url: p.url, title: p.title }; })
      .filter(function (p) { return p.slug; });
    list.sort(function (a, b) {
      var ia = ORDER.indexOf(a.slug), ib = ORDER.indexOf(b.slug);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return list;
  }

  function labelFor(p) { return (POLICIES[p.slug] && POLICIES[p.slug].label) || p.title; }

  function isShort(node) {
    if (node.tagName !== 'P') return false;
    var first = node.firstElementChild;
    var lead = node.textContent.trim();
    return /^in short\b/i.test(lead) && first && (first.tagName === 'STRONG' || first.tagName === 'B') && /^in short/i.test(first.textContent.trim());
  }

  function decorateShort(node) {
    var first = node.firstElementChild;
    first.parentNode.removeChild(first);
    var next = node.firstChild;
    if (next && next.nodeType === 3) next.textContent = next.textContent.replace(/^\s*:?\s*/, '');
    node.classList.add('agp-short');
    node.insertBefore(el('span', { class: 'agp-short__label', text: 'In short' }), node.firstChild);
  }

  function markConfirm(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var hits = [];
    while (walker.nextNode()) { if (/\[CONFIRM[^\]]*\]/.test(walker.currentNode.nodeValue)) hits.push(walker.currentNode); }
    hits.forEach(function (textNode) {
      var frag = document.createDocumentFragment();
      textNode.nodeValue.split(/(\[CONFIRM[^\]]*\])/).forEach(function (part) {
        if (!part) return;
        frag.appendChild(/^\[CONFIRM/.test(part) ? el('mark', { class: 'agp-confirm', text: part }) : document.createTextNode(part));
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  function labelTable(table) {
    var heads = Array.prototype.map.call(table.querySelectorAll('thead th, tr:first-child th'), function (th) { return th.textContent.trim(); });
    if (!heads.length) return;
    table.setAttribute('data-cols', String(heads.length));
    Array.prototype.forEach.call(table.querySelectorAll('tbody tr'), function (tr) {
      Array.prototype.forEach.call(tr.children, function (cell, i) { if (heads[i] && cell.tagName === 'TD') cell.setAttribute('data-label', heads[i]); });
    });
  }

  var linkIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>';

  function build() {
    var container = document.querySelector('.shopify-policy__container');
    if (!container || container.classList.contains('agp')) return;
    var body = container.querySelector('.shopify-policy__body');
    var heading = container.querySelector('.shopify-policy__title h1') || container.querySelector('h1');
    if (!body) { container.classList.add('agp-ready'); return; }

    var data = readData();
    var slug = slugFromPath(data.current || window.location.pathname);
    var meta = POLICIES[slug] || {};
    var policies = policyList(data);
    var email = data.email || 'support@arcticgrey.com';

    var rte = body.querySelector('.rte') || body;
    normalizeLegacy(rte);
    var nodes = flatten(rte);
    var words = (rte.textContent || '').trim().split(/\s+/).length;
    var minutes = Math.max(1, Math.round(words / 230));

    var updated = '';
    var lead = null;
    var highlights = null;
    var preamble = [];
    var sections = [];
    var current = null;

    function isHighlightList(node) {
      if (node.tagName !== 'UL' || !node.children.length) return false;
      return Array.prototype.every.call(node.children, function (li) {
        var first = li.firstElementChild;
        return li.tagName === 'LI' && !li.querySelector('ul, ol') && first && (first.tagName === 'STRONG' || first.tagName === 'B') &&
          li.textContent.trim().indexOf(first.textContent.trim()) === 0;
      });
    }

    nodes.forEach(function (node) {
      if (node.tagName === 'H2') {
        current = { heading: node, nodes: [] };
        sections.push(current);
        return;
      }
      if (current) { current.nodes.push(node); return; }
      var text = node.textContent.trim();
      if (!updated && node.tagName === 'P' && /^last updated/i.test(text)) { updated = text.replace(/^last updated\s*:?\s*/i, ''); return; }
      if (!lead && !preamble.length && node.tagName === 'P' && text && !isShort(node)) { lead = node; return; }
      if (!highlights && lead && !preamble.length && isHighlightList(node)) { highlights = node; return; }
      preamble.push(node);
    });

    container.classList.add('agp');
    if (!sections.length) container.classList.add('agp--simple');

    /* A lead longer than a couple of lines reads as body copy: it opens the article and the hero uses the summary. */
    var heroLead = null;
    if (lead && lead.textContent.trim().length <= LEAD_MAX) heroLead = lead.innerHTML;
    else if (lead) { lead.classList.add('agp-intro'); preamble.unshift(lead); }
    if (!heroLead && meta.summary) heroLead = meta.summary.replace(/&/g, '&amp;').replace(/</g, '&lt;');

    /* Contact-style pages without sections show their highlight list as a details list, not numbered cards. */
    if (highlights && !sections.length) {
      var at = preamble[0] && preamble[0].classList && preamble[0].classList.contains('agp-intro') ? 1 : 0;
      preamble.splice(at, 0, detailsFrom(Array.prototype.map.call(highlights.children, function (li) {
        var strong = li.firstElementChild;
        var label = strong.textContent.trim().replace(/[.:]\s*$/, '');
        strong.parentNode.removeChild(strong);
        return { label: label, html: li.innerHTML.trim() };
      })));
      highlights = null;
    }

    /* Hero */
    var titleText = meta.title || ((heading && heading.textContent.trim()) || document.title);
    if (!/[.!?]$/.test(titleText)) titleText += '.';
    var title = el('h1', { class: 'agp-title', text: titleText });
    var heroMain = el('div', { class: 'agp-hero__main' }, [el('p', { class: 'agp-kicker', text: 'Arctic Grey · Legal' }), title]);
    if (heroLead) heroMain.appendChild(el('p', { class: 'agp-lead', html: heroLead }));

    var metaList = el('dl', { class: 'agp-meta' });
    if (updated) metaList.appendChild(el('div', null, [el('dt', { text: 'Last updated' }), el('dd', { text: updated })]));
    if (minutes >= 2) metaList.appendChild(el('div', null, [el('dt', { text: 'Reading time' }), el('dd', { text: minutes + ' min' })]));
    if (sections.length > 1) metaList.appendChild(el('div', { class: 'agp-meta__sections' }, [el('dt', { text: 'Sections' }), el('dd', { text: String(sections.length) })]));

    var heroGrid = el('div', { class: 'agp-wrap agp-hero__grid' }, [heroMain, metaList.children.length ? metaList : null]);
    var heroParts = [heroGrid];
    if (policies.length > 1) {
      var nav = el('nav', { class: 'agp-switch', 'aria-label': 'Arctic Grey policies' });
      policies.forEach(function (p) {
        var a = el('a', { href: p.url, text: labelFor(p) });
        if (p.slug === slug) a.setAttribute('aria-current', 'page');
        nav.appendChild(a);
      });
      heroParts.push(el('div', { class: 'agp-wrap' }, [nav]));
    }
    var hero = el('header', { class: 'agp-hero' }, heroParts);

    /* Highlights */
    var cardsWrap = null;
    if (highlights) {
      var cards = el('ul', { class: 'agp-cards', role: 'list', 'data-count': String(highlights.children.length) });
      Array.prototype.forEach.call(highlights.children, function (li, i) {
        var strong = li.querySelector('strong, b');
        var cardTitle = strong ? strong.textContent.trim() : '';
        if (strong) strong.parentNode.removeChild(strong);
        cards.appendChild(el('li', { class: 'agp-card' }, [
          el('span', { class: 'agp-card__num', 'aria-hidden': 'true', text: pad(i + 1) }),
          cardTitle ? el('h2', { class: 'agp-card__title', text: cardTitle }) : null,
          el('p', { class: 'agp-card__text', html: li.innerHTML.trim() })
        ]));
      });
      cardsWrap = el('div', { class: 'agp-wrap' }, [cards]);
    }

    /* Article and contents */
    var used = {};
    var article = el('article', { class: 'agp-article' });
    var tocList = el('ol');
    var links = [];

    if (preamble.length) {
      var pre = el('div', { class: 'agp-preamble' });
      preamble.forEach(function (n) { pre.appendChild(n); });
      article.appendChild(pre);
    }

    sections.forEach(function (s, i) {
      var text = s.heading.textContent.trim().replace(/^\d+(\.\d+)*\s*[.:)]?\s*/, '');
      var id = s.heading.id || slugify(text, used);
      var h2 = el('h2', { id: id + '-title', text: text });
      var anchor = el('button', { type: 'button', class: 'agp-anchor', 'aria-label': 'Copy link to ' + text, 'data-target': id, html: linkIcon });
      var sec = el('section', { class: 'agp-sec', id: id, 'aria-labelledby': id + '-title' }, [
        el('div', { class: 'agp-sec__head' }, [el('span', { class: 'agp-sec__num', 'aria-hidden': 'true', text: pad(i + 1) }), h2, anchor])
      ]);
      s.nodes.forEach(function (n) {
        if (isShort(n)) decorateShort(n);
        if (n.tagName === 'TABLE') { labelTable(n); sec.appendChild(el('div', { class: 'agp-table' }, [n])); return; }
        sec.appendChild(n);
      });
      article.appendChild(sec);

      var link = el('a', { href: '#' + id }, [el('span', { class: 'agp-toc__n', 'aria-hidden': 'true', text: pad(i + 1) }), el('span', { text: text })]);
      links.push({ link: link, section: sec, text: text });
      tocList.appendChild(el('li', null, [link]));
    });

    var toc = null;
    var tocCurrent = null;
    var toggle = null;
    if (sections.length) {
      tocCurrent = el('span', { class: 'agp-toc__current', text: links[0].text });
      toggle = el('button', { type: 'button', class: 'agp-toc__toggle', 'aria-expanded': 'false', 'aria-controls': 'agp-toc-panel' }, [
        el('span', { class: 'agp-toc__label', text: 'On this page' }), tocCurrent, el('span', { class: 'agp-toc__chev', 'aria-hidden': 'true', html: chevron })
      ]);
      var aside = el('div', { class: 'agp-toc__aside' }, [
        el('p', { html: 'Questions? <a href="mailto:' + email + '">' + email + '</a>', style: 'margin:0' }),
        el('button', { type: 'button', class: 'agp-toc__print', 'data-agp-print': '', text: 'Print or save as PDF' })
      ]);
      toc = el('nav', { class: 'agp-toc', 'aria-label': 'On this page', 'data-open': 'false' }, [
        toggle,
        el('div', { class: 'agp-toc__panel', id: 'agp-toc-panel' }, [el('p', { class: 'agp-toc__heading', text: 'On this page' }), tocList, aside])
      ]);
    }

    var layout = el('div', { class: 'agp-wrap agp-layout' }, [toc, article]);

    /* Closing */
    var list = el('ul', { class: 'agp-closing__list', role: 'list' });
    policies.forEach(function (p) {
      var a = el('a', { href: p.url, text: (POLICIES[p.slug] && POLICIES[p.slug].title.replace(/\.$/, '')) || p.title });
      if (p.slug === slug) a.setAttribute('aria-current', 'page');
      list.appendChild(el('li', null, [a]));
    });
    var closing = el('section', { class: 'agp-closing', 'aria-labelledby': 'agp-closing-title' }, [
      el('div', { class: 'agp-wrap agp-closing__grid' }, [
        el('div', null, [
          el('h2', { class: 'agp-closing__title', id: 'agp-closing-title', text: 'Questions about this policy?' }),
          el('p', { class: 'agp-closing__text', text: 'Write to us and we will walk you through it in plain English. A signed agreement with Arctic Grey always takes precedence over these online policies.' }),
          el('div', { class: 'agp-actions' }, [
            el('a', { class: 'agp-btn agp-btn--gold', href: 'mailto:' + email, text: email }),
            el('button', { type: 'button', class: 'agp-btn agp-btn--ghost', 'data-agp-print': '', text: 'Print or save as PDF' })
          ]),
          el('p', { class: 'agp-closing__privacy' }, [
            el('a', { href: '#', 'data-ag-cookie-prefs': '', text: 'Cookie preferences' }),
            el('span', { 'aria-hidden': 'true', text: ' · ' }),
            el('a', { href: '/pages/ccpa-opt-out', text: 'Your privacy choices' }),
            el('span', { 'aria-hidden': 'true', text: ' · ' }),
            el('a', { href: '/pages/legal-archive', text: 'Earlier versions' })
          ]),
          el('a', { class: 'agp-top', href: '#', 'data-agp-top': '', text: 'Back to top ↑' })
        ]),
        policies.length > 1 ? el('div', null, [el('p', { class: 'agp-closing__list-title', text: 'All policies' }), list]) : null
      ])
    ]);

    var progress = el('div', { class: 'agp-progress', 'aria-hidden': 'true' });
    var toast = el('div', { class: 'agp-toast', role: 'status', 'aria-live': 'polite' });

    while (container.firstChild) container.removeChild(container.firstChild);
    [progress, hero, cardsWrap, layout, closing, toast].forEach(function (n) { if (n) container.appendChild(n); });
    markConfirm(container);
    container.classList.add('agp-ready');

    wire(container, { links: links, toc: toc, toggle: toggle, tocCurrent: tocCurrent, article: article, progress: progress, toast: toast });
  }

  function wire(container, ui) {
    var header = document.querySelector('sticky-header') || document.querySelector('.shopify-section-header');
    var headerBottom = -1;
    var ticking = false;
    var toastTimer = null;

    function measureHeader() {
      var b = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      if (b !== headerBottom) { headerBottom = b; container.style.setProperty('--agp-header', b + 'px'); }
    }

    function update() {
      ticking = false;
      measureHeader();
      var r = ui.article.getBoundingClientRect();
      var span = r.height - window.innerHeight + headerBottom;
      var p = span > 0 ? Math.min(1, Math.max(0, (headerBottom - r.top) / span)) : 0;
      ui.progress.style.transform = 'scaleX(' + p.toFixed(4) + ')';

      if (!ui.links.length) return;
      var line = headerBottom + 140;
      var active = ui.links[0];
      for (var i = 0; i < ui.links.length; i++) {
        if (ui.links[i].section.getBoundingClientRect().top <= line) active = ui.links[i];
        else break;
      }
      if (active !== ui.active) {
        if (ui.active) ui.active.link.removeAttribute('aria-current');
        active.link.setAttribute('aria-current', 'true');
        if (ui.tocCurrent) ui.tocCurrent.textContent = active.text;
        ui.active = active;
        keepVisible(active.link);
      }
      tocEdge();
    }

    function keepVisible(link) {
      var box = ui.toc;
      if (!box || box.scrollHeight <= box.clientHeight + 1) return;
      var top = link.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
      var pad = 48;
      if (top < box.scrollTop + pad) box.scrollTop = Math.max(0, top - pad);
      else if (top + link.offsetHeight > box.scrollTop + box.clientHeight - pad) box.scrollTop = top + link.offsetHeight - box.clientHeight + pad;
    }

    function tocEdge() {
      if (!ui.toc) return;
      var more = ui.toc.scrollHeight - ui.toc.clientHeight - ui.toc.scrollTop > 4;
      if (more) ui.toc.setAttribute('data-more', '');
      else ui.toc.removeAttribute('data-more');
    }

    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }

    function setOpen(open) {
      if (!ui.toc) return;
      ui.toc.setAttribute('data-open', open ? 'true' : 'false');
      ui.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function goTo(id, push, instant) {
      var target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: instant || reduceMotion ? 'auto' : 'smooth', block: 'start' });
      if (push && window.history && window.history.pushState) window.history.pushState(null, '', '#' + id);
    }

    function showToast(msg) {
      ui.toast.textContent = msg;
      ui.toast.setAttribute('data-show', 'true');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(function () { ui.toast.setAttribute('data-show', 'false'); }, 1800);
    }

    if (ui.toggle) {
      ui.toggle.addEventListener('click', function () { setOpen(ui.toc.getAttribute('data-open') !== 'true'); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setOpen(false); });
      document.addEventListener('click', function (e) { if (ui.toc && !ui.toc.contains(e.target)) setOpen(false); });
    }

    ui.links.forEach(function (item) {
      item.link.addEventListener('click', function (e) {
        e.preventDefault();
        setOpen(false);
        goTo(item.section.id, true);
      });
    });

    container.addEventListener('click', function (e) {
      var anchor = e.target.closest('.agp-anchor');
      if (anchor) {
        var id = anchor.getAttribute('data-target');
        var url = window.location.href.split('#')[0] + '#' + id;
        if (window.history && window.history.replaceState) window.history.replaceState(null, '', '#' + id);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () { showToast('Link copied'); }, function () { showToast('Link ready in the address bar'); });
        } else { showToast('Link ready in the address bar'); }
        return;
      }
      if (e.target.closest('[data-agp-print]')) { window.print(); return; }
      if (e.target.closest('[data-agp-top]')) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    });

    if (ui.toc) ui.toc.addEventListener('scroll', tocEdge, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    if (window.location.hash.length > 1) {
      var initial = decodeURIComponent(window.location.hash.slice(1));
      var settle = function () {
        var target = document.getElementById(initial);
        if (target && Math.abs(target.getBoundingClientRect().top - headerBottom) > 120) goTo(initial, false, true);
      };
      window.setTimeout(settle, 60);
      if (document.readyState === 'complete') window.setTimeout(settle, 400);
      else window.addEventListener('load', function () { window.setTimeout(settle, 100); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();
})();
